import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { auth, db } from '../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  AccessibilitySettings,
  DEFAULT_ACCESSIBILITY_SETTINGS,
  ACCESSIBILITY_CSS_CLASSES,
  COLORBLIND_MODE_CLASSES,
  LARGE_TEXT_MIN_PX,
  DYSLEXIA_MIN_LETTER_SPACING,
  DYSLEXIA_MIN_LINE_SPACING,
} from '../config/accessibilityConfig';

interface AccessibilityContextType {
  settings: AccessibilitySettings;
  updateSettings: (settings: Partial<AccessibilitySettings>) => Promise<void>;
  resetSettings: () => Promise<void>;
  isLoading: boolean;
  /**
   * S114: how the settings on screen were arrived at when the server read
   * failed. `null` on the normal path. 'defaults' means what is displayed is the
   * platform default, NOT this child's configuration — and a save from that
   * state would overwrite the settings they actually had.
   */
  loadFailed: 'defaults' | 'device-copy' | null;
  /** S114: true when the last save reached this device but not the server. */
  saveFailed: boolean;
}

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

/**
 * S69: this was a single device-global key, `'ns-gaming-a11y-settings'`.
 *
 * A shared classroom device runs many children through one browser. The signed-in path
 * does read `userAccessibility/{uid}` from Firestore, so a child WITH saved settings is
 * fine — but a child with none fell through to this key and inherited whichever child
 * used the machine last: their dyslexia mode, their high-contrast theme, their reduced
 * motion. On the accessibility surface specifically, that is both a privacy signal about
 * the previous child and a usability failure for this one.
 *
 * Same class as S61 (offline drafts) and S12 (the project cache): standing rule 9b, any
 * per-viewer store needs the viewer in the key.
 */
const storageKeyFor = (uid: string | undefined) =>
  uid ? `ns-gaming-a11y-settings_${uid}` : 'ns-gaming-a11y-settings_anon';

/**
 * S85: the SVG filters the colour-blind modes reference, actually created.
 *
 * `accessibility.css` states at the top of the file that
 * "SVG filter definitions (#protanopia-filter, #deuteranopia-filter,
 * #tritanopia-filter) are injected into the DOM by AccessibilityContext.tsx
 * component at runtime". They were not. There was no `<filter>` element, no
 * `feColorMatrix` and no `createElementNS` anywhere in `src/`, `public/` or
 * `index.html`, so `body.style.filter = 'url(#protanopia-filter)'` referenced an
 * id that did not resolve. A colour-blind child picked their mode, the choice was
 * saved and survived sign-out, and the page never changed.
 *
 * These are DALTONIZATION matrices, not simulation matrices — they shift the
 * colours a given form of colour blindness cannot separate into channels it can.
 * Simulating protanopia for a protanopic user would make the product harder to
 * read, not easier, which is worth stating because the original comment said
 * "color blind vision simulation".
 */
const COLOR_BLIND_FILTERS: ReadonlyArray<{ id: string; matrix: string }> = [
  {
    id: 'protanopia-filter',
    matrix: '0 2.02344 -2.52581 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 1 0',
  },
  {
    id: 'deuteranopia-filter',
    matrix: '1 0 0 0 0  0.494207 0 1.24827 0 0  0 0 1 0 0  0 0 0 1 0',
  },
  {
    id: 'tritanopia-filter',
    matrix: '1 0 0 0 0  0 1 0 0 0  -0.395913 0.801109 0 0 0  0 0 0 1 0',
  },
];

const FILTER_HOST_ID = 'a11y-colorblind-filters';


/** Idempotent: creates the host <svg> once, then does nothing. */
export const ensureColorBlindFilters = (): void => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(FILTER_HOST_ID)) return;

  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('id', FILTER_HOST_ID);
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  // Present but not laid out: `display: none` would stop some engines resolving
  // the filter reference, so it is hidden by size and position instead.
  svg.setAttribute(
    'style',
    'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none'
  );

  for (const { id, matrix } of COLOR_BLIND_FILTERS) {
    const filter = document.createElementNS(NS, 'filter');
    filter.setAttribute('id', id);
    filter.setAttribute('color-interpolation-filters', 'linearRGB');

    const colorMatrix = document.createElementNS(NS, 'feColorMatrix');
    colorMatrix.setAttribute('type', 'matrix');
    colorMatrix.setAttribute('values', matrix);

    filter.appendChild(colorMatrix);
    svg.appendChild(filter);
  }

  document.body.appendChild(svg);
};

export const AccessibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(DEFAULT_ACCESSIBILITY_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  // S114: 'defaults' when the read failed and there was no device copy either,
  // so what is on screen is the platform default rather than the child's
  // configuration; 'device-copy' when a possibly-stale local copy is showing.
  const [loadFailed, setLoadFailed] = useState<'defaults' | 'device-copy' | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  /**
   * S114: what the child changed while the stored settings were still in
   * flight. `setSettings(firestoreSettings)` used to overwrite it wholesale, so
   * on a school connection a child who needs high contrast could open the panel,
   * correctly see it off, turn it on, watch the page repaint — and watch it
   * silently revert a moment later with no message. Their change wins now, and
   * is saved.
   */
  const pendingChangesRef = useRef<Partial<AccessibilitySettings>>({});

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const currentUser = auth.value.currentUser;

        // Try to load from Firestore if user is logged in
        if (currentUser?.uid) {
          const userAccessibilityRef = doc(db.value, 'userAccessibility', currentUser.uid);
          const userAccessibilitySnap = await getDoc(userAccessibilityRef);

          if (userAccessibilitySnap.exists()) {
            const stored = userAccessibilitySnap.data() as AccessibilitySettings;
            const firestoreSettings = { ...stored, ...pendingChangesRef.current };
            setSettings(firestoreSettings);
            localStorage.setItem(storageKeyFor(currentUser.uid), JSON.stringify(firestoreSettings));
            applySettings(firestoreSettings);
            setIsLoading(false);
            return;
          }
        }

        // Fall back to this viewer's own localStorage entry. Never a shared one: a
        // child with no saved settings must get the defaults, not the last child's.
        const stored = localStorage.getItem(storageKeyFor(currentUser?.uid));
        if (stored) {
          const parsedSettings = JSON.parse(stored) as AccessibilitySettings;
          setSettings(parsedSettings);
          applySettings(parsedSettings);
        } else {
          applySettings(DEFAULT_ACCESSIBILITY_SETTINGS);
        }
      } catch (error) {
        // S114: this fell back to localStorage and then to the platform
        // defaults, and the panel had no notion of an error — so a denied read
        // rendered exactly the screen of a brand-new account, every switch the
        // child relies on off. Touching a single control then spread over those
        // DEFAULTS and wrote the whole document back, promoting the failed read
        // into a save that erased the settings they actually had.
        console.error('Error loading accessibility settings:', error);
        const stored = localStorage.getItem(storageKeyFor(auth.value.currentUser?.uid));
        if (stored) {
          const parsedSettings = JSON.parse(stored) as AccessibilitySettings;
          setSettings(parsedSettings);
          applySettings(parsedSettings);
          // A device copy IS the child's own configuration, so this is not a
          // silent default — but it may be stale, and the server copy is the one
          // a save would overwrite.
          setLoadFailed('device-copy');
        } else {
          applySettings(DEFAULT_ACCESSIBILITY_SETTINGS);
          setLoadFailed('defaults');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
    // applySettings only reads its argument and writes to the DOM (no reactive
    // deps); this effect is intentionally a one-time mount loader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply settings to DOM
  const applySettings = (newSettings: AccessibilitySettings) => {
    const body = document.body;

    // Remove all accessibility classes first
    Object.values(ACCESSIBILITY_CSS_CLASSES).forEach((className) => {
      body.classList.remove(className);
    });

    // Apply new classes
    if (newSettings.highContrast) {
      body.classList.add(ACCESSIBILITY_CSS_CLASSES.highContrast);
    }
    if (newSettings.largeText) {
      body.classList.add(ACCESSIBILITY_CSS_CLASSES.largeText);
    }
    if (newSettings.dyslexiaFont) {
      body.classList.add(ACCESSIBILITY_CSS_CLASSES.dyslexiaFont);
    }
    if (newSettings.reducedMotion) {
      body.classList.add(ACCESSIBILITY_CSS_CLASSES.reducedMotion);
    }
    if (newSettings.largeClickTargets) {
      body.classList.add(ACCESSIBILITY_CSS_CLASSES.largeTargets);
    }
    if (newSettings.screenReaderOptimized) {
      body.classList.add(ACCESSIBILITY_CSS_CLASSES.screenReaderOptimized);
    }
    // S114: `accessibility.css` defines `.a11y-keyboard-focus *:focus` with a
    // thick outline and `ACCESSIBILITY_CSS_CLASSES.keyboardFocus` names it —
    // and this function had a branch for six of the seven classes in that map
    // and none for this one. So the box a child who cannot use a mouse was told
    // to tick stayed ticked across sessions and changed nothing.
    if (newSettings.keyboardOnly) {
      body.classList.add(ACCESSIBILITY_CSS_CLASSES.keyboardFocus);
    }

    // Apply CSS variables for dynamic values.
    //
    // S85: "Large Text" could not change body text size. `.a11y-large-text` set
    // `--a11y-font-size: 18px` as a CLASS rule on <body>, and this line writes the
    // same custom property as an INLINE style on the same element — inline wins, so
    // the class's 18px was dead. (`.a11y-large-text body` could not match either:
    // the class is ON body, and that selector needs a <body> nested inside it.) So
    // the checkbox enlarged headings, whose rules are `!important`, and left
    // paragraph text at whatever the slider said — while the panel promised it
    // "makes all text larger throughout the application".
    //
    // The inline property is the single source of truth, so Large Text raises it.
    const effectiveFontSize = newSettings.largeText
      ? Math.max(newSettings.fontSize, LARGE_TEXT_MIN_PX)
      : newSettings.fontSize;
    body.style.setProperty('--a11y-font-size', `${effectiveFontSize}px`);

    // S134: the same shape one row down. `accessibility.css` asks for the
    // spacing half of Dyslexia-Friendly Text as CSS variable FALLBACKS —
    // `var(--a11y-letter-spacing, 0.05em)` and `var(--a11y-line-spacing, 1.5)` —
    // and a fallback applies only when the variable is UNSET. These two lines
    // set it unconditionally, on mount, from the defaults 0em and 1.5, so the
    // 0.05em was never reached and the 1.5 was the body's own line height. With
    // the typeface not bundled either (S24), ticking the box on a Chromebook
    // changed nothing at all. Floors, like Large Text's, so a child who has
    // already dragged the sliders higher keeps what they chose.
    const effectiveLineSpacing = newSettings.dyslexiaFont
      ? Math.max(newSettings.lineSpacing, DYSLEXIA_MIN_LINE_SPACING)
      : newSettings.lineSpacing;
    const effectiveLetterSpacing = newSettings.dyslexiaFont
      ? Math.max(newSettings.letterSpacing, DYSLEXIA_MIN_LETTER_SPACING)
      : newSettings.letterSpacing;
    body.style.setProperty('--a11y-line-spacing', `${effectiveLineSpacing}`);
    body.style.setProperty('--a11y-letter-spacing', `${effectiveLetterSpacing}em`);
    body.style.setProperty('--a11y-tts-rate', `${newSettings.ttsRate}`);

    // Apply colour-blind mode.
    //
    // S85: there were THREE mechanisms for this feature and none worked. The
    // stylesheet defines `body.a11y-colorblind-protanopia` and friends, which no
    // TypeScript file ever added; this function wrote `body.style.filter` inline;
    // and both referenced SVG filter ids that did not exist anywhere.
    //
    // One mechanism now: the filters are injected, and the CLASS is what selects
    // them, so presentation stays in the stylesheet. The inline `filter` is still
    // cleared on the way through, because builds before this one set it.
    for (const className of Object.values(COLORBLIND_MODE_CLASSES)) {
      if (className) body.classList.remove(className);
    }
    body.style.removeProperty('filter');

    const colorBlindClass =
      COLORBLIND_MODE_CLASSES[
        newSettings.colorBlindMode as keyof typeof COLORBLIND_MODE_CLASSES
      ] ?? null;
    if (colorBlindClass) {
      ensureColorBlindFilters();
      body.classList.add(colorBlindClass);
    }

    // Mark document for screen readers.
    //
    // S114: this had no `else`, the CLAUDE.md "an `if (x)` with no else" tell, on
    // the one surface where the mistake is read aloud. An `aria-label` on <body>
    // renames the whole document for assistive technology, so a child (or a
    // teacher on a shared classroom machine) who tried the mode and turned it
    // back off was left with every screen announcing "NS Gaming Studio -
    // Accessibility optimized" as the document's name, with no control that
    // could remove it: the checkbox unticked, the class gone, the label not.
    if (newSettings.screenReaderOptimized) {
      body.setAttribute('aria-label', 'NS Gaming Studio - Accessibility optimized');
    } else {
      body.removeAttribute('aria-label');
    }
  };

  const updateSettings = async (newSettings: Partial<AccessibilitySettings>) => {
    pendingChangesRef.current = { ...pendingChangesRef.current, ...newSettings };
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);
    applySettings(updatedSettings);

    // Save to localStorage
    localStorage.setItem(
      storageKeyFor(auth.value.currentUser?.uid),
      JSON.stringify(updatedSettings)
    );

    // Save to Firestore if user is logged in
    try {
      const currentUser = auth.value.currentUser;
      if (currentUser?.uid) {
        const userAccessibilityRef = doc(db.value, 'userAccessibility', currentUser.uid);
        await setDoc(userAccessibilityRef, updatedSettings, { merge: true });
      }
      setSaveFailed(false);
    } catch (error) {
      // S114: the only action here was `console.error`. The switch was already
      // flipped and the page already repainted, so a child saw their setting
      // take effect on this device and had no way to learn it was not stored —
      // and on a shared classroom Chromebook they signed in tomorrow, or on the
      // next machine, to find every accessibility setting they had configured
      // gone, with nothing having reported a problem at any point. Rule 7.
      console.error('Error saving accessibility settings to Firestore:', error);
      setSaveFailed(true);
    }
  };

  const resetSettings = async () => {
    pendingChangesRef.current = {};
    setSettings(DEFAULT_ACCESSIBILITY_SETTINGS);
    applySettings(DEFAULT_ACCESSIBILITY_SETTINGS);

    // Clear localStorage
    localStorage.removeItem(storageKeyFor(auth.value.currentUser?.uid));
    // Also clear the pre-S69 device-global key, so nothing unattributable is left.
    localStorage.removeItem('ns-gaming-a11y-settings');

    // Clear Firestore if user is logged in
    try {
      const currentUser = auth.value.currentUser;
      if (currentUser?.uid) {
        const userAccessibilityRef = doc(db.value, 'userAccessibility', currentUser.uid);
        await setDoc(userAccessibilityRef, DEFAULT_ACCESSIBILITY_SETTINGS, { merge: true });
      }
    } catch (error) {
      console.error('Error resetting accessibility settings in Firestore:', error);
    }
  };

  return (
    <AccessibilityContext.Provider
      value={{
        settings,
        updateSettings,
        resetSettings,
        isLoading,
        loadFailed,
        saveFailed,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = (): AccessibilityContextType => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};

/**
 * The settings, or the defaults when there is no provider above.
 *
 * S133: `useKeyboardShortcuts` needs one setting (`stickyKeys`) and is used by
 * leaf components that have no reason to know about this context. Making the
 * hook throw outside a provider would turn a small accessibility feature into a
 * mounting constraint on every consumer — and the honest fallback is not a
 * guess: DEFAULT_ACCESSIBILITY_SETTINGS is what a child who has configured
 * nothing gets anyway, and it fails toward "no special behaviour".
 */
export const useAccessibilitySettings = (): AccessibilitySettings =>
  useContext(AccessibilityContext)?.settings ?? DEFAULT_ACCESSIBILITY_SETTINGS;
